// Copyright (c) Move Closer 2022.
// Maciej  tu byl ;)
import { AnyObject, Injectable, ResourceActionFailed } from '@movecloser/front-core'

import {
  CartBillingAddress,
  CartIdentifier,
  CartShippingAddress, IGraphQL,
  MagentoCartCountry,
  OrderResponse,
  PaymentMethodPayload,
  ShippingDetails,
  ShippingMethod
} from '../../../../contexts'

import { IProductsRepository } from '../../../products/contracts/repositories'
import { resolveFromStatus } from '../../../../support'

import { CartModel, OrderStatus, Przelewy24TransactionOutput } from '../../contracts'
import { Przelewy24PaymentMethodDefinition } from '../../organisms/PaymentStep/PaymentStep.contracts'

import { CartAwareAbstract } from '../cart-aware.abstract'
import { CheckoutModuleConfig, ICheckoutService } from './contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export class CheckoutService extends CartAwareAbstract implements ICheckoutService {
  protected readonly config: CheckoutModuleConfig

  constructor (connector: IGraphQL, products: IProductsRepository, config: CheckoutModuleConfig) {
    super(connector, products)
    this.config = config
  }

  public async applyGiftCard (cartId: CartIdentifier, giftCardCode: string): Promise<CartModel> {
    const response = await this.connector.call('applyAmGiftCardToCart', {
      input: {
        am_gift_card_code: giftCardCode,
        cart_id: cartId
      }
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.composeCart(response.data.applyAmGiftCardToCart.cart, false, this.config)
  }

  public async removeGiftCard (cartId: CartIdentifier, giftCardCode: string): Promise<CartModel> {
    const response = await this.connector.call('removeAmGiftCardFromCart', {
      input: {
        am_gift_card_code: giftCardCode,
        cart_id: cartId
      }
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.composeCart(response.data.removeAmGiftCardFromCart.cart, false, this.config)
  }

  public async loadCountries (): Promise<MagentoCartCountry[]> {
    const response = await this.connector.call('getCountries', {})

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.countries
  }

  public async loadPaymentMethods (amount: number): Promise<Przelewy24PaymentMethodDefinition[]> {
    const response = await this.connector.call('loadPrzelewy24PaymentMethods', { total: amount })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.przelewy24PaymentMethods
  }

  public async placeOrder (cartId: CartIdentifier, reviewsRequestConsent?: boolean): Promise<OrderResponse> {
    const response = await this.connector.call(
      'placeOrder',
      { cartId, reviewsRequestConsent }
    )

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const order = response.data.placeOrder.order
    if (!order) {
      throw new ResourceActionFailed(
        'Missing order data',
        500
      )
    }

    return {
      orderNumber: order.order_number,
      instructions: order.instructions,
      paymentMethod: order.payment_method,
      payuRedirectUri: order.payu_redirect_uri,
      paynowRedirectUri: order.paynow_redirect_uri,
      przelewy24PaymentUrl: order.przelewy24PaymentUrl
    }
  }

  public async registerPrzelewy24BlikTransaction (input: AnyObject): Promise<Przelewy24TransactionOutput> {
    const response = await this.connector.call('registerPrzelewy24BlikTransaction', input)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.registerPrzelewy24BlikTransaction
  }

  public async registerPrzelewy24GooglePayTransaction (input: AnyObject): Promise<Przelewy24TransactionOutput> {
    const response = await this.connector.call('registerPrzelewy24GooglePayTransaction', input)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.registerPrzelewy24GooglePayTransaction
  }

  public async setBillingAddress (cartId: CartIdentifier, cartAddress: CartBillingAddress): Promise<CartModel> {
    const { countryCode, sameAsShipping, vatId, ...rest } = cartAddress
    const address = {
      same_as_shipping: sameAsShipping,
      address: { ...rest, country_code: countryCode, vat_id: vatId }
    }
    const response = await this.connector.call('setBillingAddress', { cartId, address })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setBillingAddressOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setGuestEmail (cartId: CartIdentifier, email: string): Promise<CartModel> {
    const response = await this.connector.call('setGuestEmail', { cartId, email })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setGuestEmailOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setInPostParcel (cartId: CartIdentifier, parcelId: string): Promise<void> {
    const response = await this.connector.call('setInPostParcel', { cartId, parcelId })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }
  }

  public async setDHLPopParcel (cartId: CartIdentifier, parcelId: string): Promise<void> {
    const response = await this.connector.call('setDhlPopLockerNumberOnCart', { cartId, parcelId })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }
  }

  public async setPaymentMethod (cartId: CartIdentifier, paymentMethod: PaymentMethodPayload, provider?: AnyObject): Promise<CartModel> {
    let requestPayload = {
      cartId,
      paymentMethod: {
        blikCode: paymentMethod.blik,
        code: paymentMethod.code
      }
    }

    if (typeof provider !== 'undefined' && Object.values(provider).length > 0) {
      requestPayload = {
        ...requestPayload,
        paymentMethod: {
          ...requestPayload.paymentMethod,
          [provider.type]: provider.data
        }
      }
    }

    const response = await this.connector.call('setPaymentMethod', {
      ...requestPayload
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setPaymentMethodOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setShippingAddress (cartId: CartIdentifier, cartAddress: CartShippingAddress, shouldSave: boolean = false): Promise<CartModel> {
    const { countryCode, customerNotes, vatId, ...rest } = cartAddress
    const address = { customer_notes: customerNotes, address: { ...rest, vat_id: vatId, country_code: countryCode, save_in_address_book: shouldSave } }
    const response = await this.connector.call('setShippingAddress', { cartId, address })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setShippingAddressesOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setShippingDetails (cartId: CartIdentifier, details: ShippingDetails): Promise<CartModel> {
    const response = await this.connector.call('setShippingDetails', {
      input: {
        cart_id: cartId,
        delivery_time: details.deliveryTime,
        disabled_payment_methods: details.disabledPaymentMethods,
        display_info: details.displayInfo ?? null
      }
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setDeliveryDetailsOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setShippingMethod (cartId: CartIdentifier, shippingMethod: ShippingMethod): Promise<CartModel> {
    const response = await this.connector.call('setShippingMethod', {
      cartId,
      shippingMethod: {
        carrier_code: shippingMethod.carrierCode,
        method_code: shippingMethod.methodCode
      }
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setShippingMethodsOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setFlightNumberOnCart (cartId: CartIdentifier, flightNo: string): Promise<CartModel> {
    const response = await this.connector.call('setFlightNumberOnCart', {
      cartId,
      flightNo
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setFlightNumberOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setSelectedAddressIdOnCart (cartId: CartIdentifier, addressId: number): Promise<CartModel> {
    const response = await this.connector.call('setSelectedAddressIdOnCart', {
      cartId,
      addressId
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setSelectedAddressIdOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async setIsSignupRequestedOnCart (cartId: CartIdentifier, isSignupRequested: boolean): Promise<CartModel> {
    const response = await this.connector.call('setIsSignupRequestedOnCart', {
      cartId,
      isSignupRequested
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    const cart = response.data.setIsSignupRequestedOnCart.cart
    if (!cart) {
      throw new ResourceActionFailed(
        'Missing cart',
        500
      )
    }

    return this.composeCart(cart, false, this.config)
  }

  public async getOrderStatus (orderNumber: string): Promise<OrderStatus> {
    const response = await this.connector.call('getOrderStatusByOrderNumber', {
      orderNumber
    })

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.getOrderStatusByOrderNumber
  }
}
